import equal from "fast-deep-equal/es6";
import { IObservableValue, observable } from "mobx";

import { debounce } from "@formant/core/common/debounce";
import {
    deserializeHash,
    serializeHash,
} from "@formant/core/common/serializeHash";
import { duration } from "@formant/core/model/duration";

const observables: { [key: string]: IObservableValue<any> } = {};
let data: any = {};
const initializedSymbol = Symbol("initialized");

window.addEventListener("hashchange", updateHash);
window.addEventListener("popstate", updateHash);
updateHash();

export function clearHashState() {
    data = {};
}

export function updateHash() {
    data = getHashData();
    Object.keys(data).forEach(key => {
        if (observables[key]) {
            observables[key].set(data[key]);
        }
    });
}

function updateData(newData: any) {
    if (!equal(data, newData)) {
        updateHashData();
    }
    data = newData;
}

const updateHashData = debounce(setHashData, 0.5 * duration.second);

function setHashData() {
    const empty = equal(data, {});
    if (empty && !window.location.hash) {
        return;
    }
    window.history.replaceState(
        undefined,
        window.document.title,
        empty ? window.location.pathname : `#${serializeHash(data)}`,
    );
}

function getHashData() {
    if (!window.location.hash) {
        return {};
    }

    try {
        return deserializeHash(window.location.hash.substring(1));
    } catch (error) {
        return {};
    }
}

export function hashObservable(
    options: {
        key?: string;
        transformer?: { to: (_: any) => any; from: (_: any) => any };
        ignore?: (value: any, target: any) => boolean;
        debug?: boolean;
        debugName?: string;
    } = {},
) {
    const {
        transformer = {
            to: x => x,
            from: x => x,
        },
        ignore = () => false,
        debug = false,
    } = options;

    const logger = debug ? console.log.bind(console) : (..._: any[]) => {};

    return (target: any, prop: string, descriptor?: PropertyDescriptor) => {
        const { key = prop, debugName = prop } = options;
        observables[key] = observables[key] || observable.box(undefined);

        const box = observables[key];
        if (data[key] !== undefined) {
            box.set(transformer.from(data[key]));
            logger(
                "hashObservable(%s)#initial = %s",
                debugName,
                JSON.stringify(data[key]),
            );
        } else {
            logger("hashObservable(%s)#initial = [empty]", debugName);
        }

        Object.defineProperty(target, prop, {
            ...descriptor,
            configurable: true,
            get() {
                const value = box.get();
                if (value === undefined) {
                    return undefined;
                }

                return transformer.from(value);
            },
            set(value: any) {
                const transformed = transformer.to(value);
                const isInitialization =
                    !this[initializedSymbol] || !this[initializedSymbol][key];
                if (isInitialization) {
                    this[initializedSymbol] = this[initializedSymbol] || {};
                    this[initializedSymbol][key] = true;
                    if (data[key] !== undefined) {
                        box.set(data[key]);
                    } else {
                        box.set(transformed);
                    }
                } else {
                    updateData({
                        ...data,
                        [key]: ignore(value, target) ? undefined : transformed,
                    });
                    box.set(transformed);
                    logger(
                        "hashObservable(%s)#set = %s",
                        debugName,
                        JSON.stringify(transformed),
                    );
                }
            },
        });
    };
}
